import React, { useState } from "react";
import { Table, Tag, Typography } from "antd";
import { 
  AppleOutlined, 
  AndroidOutlined, 
  WindowsOutlined, 
  ChromeOutlined, 
  MobileOutlined,
  DesktopOutlined,
  QuestionOutlined 
} from "@ant-design/icons";

const UserEventsTab = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Helper function to get device icon based on brand/OS
  const getDeviceIcon = (device) => {
    const brand = device?.device?.brand?.toLowerCase() || "";
    const os = device?.os?.family?.toLowerCase() || "";
    const ua = device?.ua?.family?.toLowerCase() || "";

    if (brand.includes("apple") || os.includes("ios") || os.includes("mac")) {
      return <AppleOutlined />;
    }
    if (brand.includes("android") || os.includes("android")) {
      return <AndroidOutlined />;
    }
    if (os.includes("windows")) {
      return <WindowsOutlined />;
    }
    if (ua.includes("chrome")) {
      return <ChromeOutlined />;
    }
    if (ua.includes("safari")) {
      return <AppleOutlined />;
    }
    if (device?.device?.isSpider) {
      return <QuestionOutlined />;
    }
    if (device?.device?.family?.toLowerCase().includes("mobile")) {
      return <MobileOutlined />;
    }
    return <DesktopOutlined />;
  };

  // Helper to format device info
  const formatDeviceInfo = (device) => {
    if (!device || !device.device) {
      return "Unknown device";
    }

    const parts = [];
    
    // Brand and model
    if (device.device.brand && device.device.model) {
      parts.push(`${device.device.brand} ${device.device.model}`);
    } else if (device.device.brand) {
      parts.push(device.device.brand);
    } else if (device.string) {
      parts.push(device.string.split("-")[0]); // Get brand from string
    }

    // OS
    if (device.os?.family) {
      parts.push(device.os.family);
    }

    // Browser
    if (device.ua?.family) {
      parts.push(device.ua.family);
    }

    return parts.length > 0 ? parts.join(" · ") : "Unknown device";
  };

  // Get device type tag color
  const getDeviceTypeColor = (device) => {
    const family = device?.device?.family?.toLowerCase() || "";
    const isMobile = family.includes("mobile") || 
                    device?.device?.brand?.toLowerCase()?.includes("iphone") ||
                    device?.device?.brand?.toLowerCase()?.includes("samsung") ||
                    family.includes("phone") ||
                    family.includes("tablet");

    if (isMobile) return "blue";
    if (device?.device?.isSpider) return "red";
    if (family.includes("desktop") || family.includes("pc")) return "purple";
    return "default";
  };

  // Get device type text
  const getDeviceType = (device) => {
    const family = device?.device?.family?.toLowerCase() || "";
    
    if (family.includes("mobile") || family.includes("phone")) return "Mobile";
    if (family.includes("tablet")) return "Tablet";
    if (family.includes("desktop") || family.includes("pc")) return "Desktop";
    if (device?.device?.isSpider) return "Bot/Crawler";
    return "Device";
  };

  // Custom pagination configuration
  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: data?.length || 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
    pageSizeOptions: ['5', '10', '20', '50', '100'],
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    onShowSizeChange: (current, size) => {
      setCurrentPage(1); // Reset to first page when changing page size
      setPageSize(size);
    },
  };

  return (
    <Table
      dataSource={data}
      rowKey={(r) => r.id}
      pagination={paginationConfig}
      columns={[
        { 
          title: "Event", 
          dataIndex: "event_type",
          render: (text) => (
            <Typography.Text strong>{text}</Typography.Text>
          )
        },
        { 
          title: "Description", 
          dataIndex: "description",
          render: (text) => (
            <Typography.Text type={text === "Success" ? "success" : "default"}>
              {text}
            </Typography.Text>
          )
        },
        { 
          title: "IP Address", 
          dataIndex: "ip_address",
          render: (ip) => (
            <Tag color="geekblue">{ip}</Tag>
          )
        },
        {
          title: "Device",
          dataIndex: "device",
          render: (device) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: '16px' }}>
                {getDeviceIcon(device)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 4 }}>
                  <Tag color={getDeviceTypeColor(device)}>
                    {getDeviceType(device)}
                  </Tag>
                </div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatDeviceInfo(device)}
                </Typography.Text>
              </div>
            </div>
          ),
        },
        {
          title: "Date",
          dataIndex: "created_at",
          render: (v) => new Date(v).toLocaleString(),
          sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
          defaultSortOrder: 'descend',
        },
      ]}
    />
  );
};

export default UserEventsTab;